import type { AccountNode } from '@taler/api-client'
import { type Locale, resolveLocalized } from '@taler/domain'

export interface AccountOption {
  readonly id: string
  readonly label: string
  readonly currency: string | undefined
}

/**
 * Every postable account, not restricted by class or currency: `account_balance` watches
 * one account chosen by the user, and the server evaluates it in the target's own currency
 * regardless of the account's own.
 */
export function flattenPostableAccounts(
  nodes: readonly AccountNode[],
  locale: Locale,
): readonly AccountOption[] {
  const out: AccountOption[] = []
  const walk = (node: AccountNode): void => {
    const { account } = node
    if (account.isPostable) {
      out.push({
        id: account.id,
        label: resolveLocalized(account.names, locale)?.name ?? account.code,
        currency: account.currency,
      })
    }
    for (const child of node.children) walk(child)
  }
  for (const node of nodes) walk(node)
  return out
}

/** Every account's display name by id, postable or not: a target may watch any account. */
export function accountLabelIndex(
  nodes: readonly AccountNode[],
  locale: Locale,
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>()
  const walk = (node: AccountNode): void => {
    const { account } = node
    labels.set(account.id, resolveLocalized(account.names, locale)?.name ?? account.code)
    for (const child of node.children) walk(child)
  }
  for (const node of nodes) walk(node)
  return labels
}
